/**
 * "Meeting point history" — the collapsible log under the meeting points list
 * (OD-1325 AC-8).
 *
 * The session type already has a full change-log tab (OD-892). This is not a
 * second copy of it: it shows only the meeting-point entries, inline, next to
 * the list they describe — which is where the question "who moved the primary,
 * and when?" is actually asked. Everything else on the session type stays in
 * the History tab.
 *
 * Entries are recognised by the field names the backend writes
 * (`meeting_points`, `meeting_point_change_N`), so nothing has to be stored
 * twice to make this view possible.
 */
import {useState} from 'react';
import {ChevronDown, ChevronRight} from 'lucide-react';
import {format, parseISO} from 'date-fns';
import {useSessionTypeHistoryQuery} from '@/shared/hooks/useSessionTypeHistoryQuery';
import type {HistoryChange, HistoryEntry} from '@/shared/types/history';

/** A change this feature wrote, as opposed to any other session-type field. */
const isMeetingPointChange = (change: HistoryChange): boolean =>
  typeof change?.field === 'string' && change.field.startsWith('meeting_point');

/** Field name as it reads in a sentence: meeting_point_zip -> "Postcode". */
const FIELD_LABELS: Record<string, string> = {
  meeting_point_name: 'Name',
  meeting_point_address: 'Address',
  meeting_point_city: 'City',
  meeting_point_state: 'State',
  meeting_point_zip: 'Postcode',
  meeting_point_country: 'Country',
};

/** A ready-made sentence written by the meeting-point list operations. */
const isSentenceChange = (change: HistoryChange): boolean =>
  typeof change?.field === 'string' &&
  change.field.startsWith('meeting_point_change_');

const quoted = (value: unknown): string => {
  const text = String(value ?? '').trim();
  return text ? `"${text}"` : 'empty';
};

/**
 * The narrative lines for one entry.
 *
 * Two shapes end up in this log, and they need opposite treatment:
 *
 *   - `meeting_point_change_N` is a ready-made sentence written by the list
 *     operations ("Added meeting point X"), so it renders as-is.
 *   - `meeting_point_<field>` is a structured old/new pair written when a
 *     meeting point's own details are edited. Rendering its `to` value alone
 *     produced the bare, contextless entries this fixes — "Bale-Valle",
 *     "Croatia" — with nothing saying which venue changed, or what about it.
 *
 * The venue's name is not in the change rows (an unchanged name produces no
 * change row at all), so it comes from the event's `details`. Entries written
 * before that was recorded fall back to naming the field without the venue,
 * which still beats a bare value.
 */
const describeEntry = (entry: HistoryEntry): string[] => {
  const changes = (entry.metadata?.changes ?? []).filter(isMeetingPointChange);
  if (changes.length === 0) return [];

  const sentences = changes.filter(isSentenceChange);
  if (sentences.length > 0) {
    return sentences.map((c) => String(c.to ?? '').trim()).filter(Boolean);
  }

  const details = (entry.metadata as Record<string, any> | undefined)?.details;
  // A rename is the one case where the "after" name would misname the entry's
  // own subject, so the previous name identifies it and the change says so.
  const renamed = changes.find((c) => c.field === 'meeting_point_name');
  const name =
    (renamed ? details?.previous_meeting_point_name : details?.meeting_point_name) ??
    details?.meeting_point_name;
  const subject = name ? `meeting point ${quoted(name)}` : 'a meeting point';

  const fieldChanges = changes.filter((c) => FIELD_LABELS[c.field]);
  if (fieldChanges.length > 0) {
    return fieldChanges.map((c) => {
      const label = FIELD_LABELS[c.field];
      return `Updated ${subject} — ${label} changed from ${quoted(c.from)} to ${quoted(c.to)}`;
    });
  }

  const summary = changes.find((c) => c.field === 'meeting_points');
  if (!summary) return [];
  return [`Meeting points changed from ${summary.from || 'None'} to ${summary.to || 'None'}`];
};

const formatWhen = (value?: string): string => {
  if (!value) return '';
  try {
    return format(parseISO(value), 'MMM d, yyyy · h:mm a');
  } catch {
    return value;
  }
};

export const MeetingPointHistory = ({sessionTypeId}: {sessionTypeId: string}) => {
  const [open, setOpen] = useState(false);
  // Only fetched once the section is opened: most visits to a session type
  // never expand it, and this sits inside an accordion that may hold many.
  const history = useSessionTypeHistoryQuery(open ? sessionTypeId : null, 50);

  const rows = (history.rows ?? [])
    .map((entry) => ({entry, lines: describeEntry(entry)}))
    .filter((row) => row.lines.length > 0);

  return (
    <div className="border-t pt-3">
      <button
        type="button"
        className="flex items-center gap-1.5 text-sm font-medium text-foreground"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        {open ? (
          <ChevronDown className="h-4 w-4" />
        ) : (
          <ChevronRight className="h-4 w-4" />
        )}
        Meeting point history
        {open && rows.length > 0 && (
          <span className="font-normal text-muted-foreground">({rows.length})</span>
        )}
      </button>

      {open && (
        <div className="mt-3 space-y-2">
          {history.isLoading && (
            <p className="text-sm text-muted-foreground">Loading history…</p>
          )}
          {history.isError && (
            <p className="text-sm text-destructive">Could not load the history.</p>
          )}
          {!history.isLoading && !history.isError && rows.length === 0 && (
            <p className="text-sm text-muted-foreground">
              No meeting point changes recorded yet. Adding, editing or
              re-designating a meeting point is recorded when you save the
              session type — until then the change is only on this screen.
            </p>
          )}
          {rows.map(({entry, lines}) =>
            lines.map((line, index) => (
              <div
                key={`${entry.id}-${index}`}
                className="grid grid-cols-[170px_1fr] gap-3 border-b border-gray-100 py-2 text-sm last:border-b-0"
              >
                <span className="text-muted-foreground">
                  {formatWhen(entry.created_at)}
                </span>
                <span className="text-foreground">
                  <b className="font-medium">
                    {entry.created_by_name || 'System'}
                  </b>{' '}
                  · {line}
                </span>
              </div>
            )),
          )}
        </div>
      )}
    </div>
  );
};

export default MeetingPointHistory;
